import React from 'react';
import { Rocket, Target, Users, Search } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const LookingFor = ({ data }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const qualities = [
    {
      icon: Rocket,
      title: "AI-Integrated Systems",
      description: "Continue building LLM observability systems, conversational AI platforms, and real-time interfaces where AI meets thoughtful UX"
    },
    {
      icon: Target,
      title: "Direct Product Impact",
      description: "Architect systems and develop full-stack features that turn complex workflows into smooth, intuitive experiences"
    },
    {
      icon: Users,
      title: "Small, Hands-On Teams", 
      description: "Thrive in environments where ownership is shared, experimentation is encouraged, and every engineer shapes the product"
    }
  ];

  return (
    <section id="looking-for" className="py-24 relative overflow-hidden">
      {/* Subtle Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/15 to-slate-900/10" />
        <div className="absolute top-28 left-28 w-64 h-64 bg-slate-400/3 rounded-full blur-3xl" />
        <div className="absolute bottom-28 right-28 w-80 h-80 bg-slate-500/2 rounded-full blur-3xl" />
      </div>
      
      <div className="container mx-auto px-6 relative z-10" ref={ref}>
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ y: 30, opacity: 0 }}
          animate={inView ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <Search className="h-8 w-8 text-slate-400" />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50">
              {data.title}
            </h2>
          </div>
          <motion.div 
            className="w-16 h-0.5 bg-slate-500 mx-auto mb-6"
            initial={{ width: 0 }}
            animate={inView ? { width: 64 } : { width: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          />
        </motion.div>

        <div className="max-w-5xl mx-auto">
          {/* Main Description */}
          <motion.div 
            className="glass-card p-8 mb-16 text-center"
            initial={{ y: 30, opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="text-lg text-slate-300 leading-relaxed">
              {data.description}
            </p>
          </motion.div>

          {/* Qualities Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {qualities.map((quality, index) => {
              const IconComponent = quality.icon;
              return (
                <motion.div
                  key={index}
                  className="glass-card p-8 text-center group"
                  initial={{ y: 30, opacity: 0 }}
                  animate={inView ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  whileHover={{ y: -4 }}
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-800/30 rounded-xl mb-6">
                    <IconComponent className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-50 mb-4">
                    {quality.title}
                  </h3>
                  <p className="text-slate-300 leading-relaxed">
                    {quality.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LookingFor;